<template>
  <div
    class="relative overflow-x-clip"
    :style="bleed ? { marginInline: 'calc(50% - 50vw)', paddingInline: 'calc(50vw - 50%)' } : {}"
  >
    <slot v-if="showArrows" name="prev" v-bind="{ active: carousel.canScrollPrev, action: scrollPrev }">
      <base-button
        :aria-label="$t.previousSlide"
        class="group absolute z-10 flex duration"
        :class="[
          {
            'pointer-events-none !op-0 invisible': (!loop || !scrollable) && !carousel.canScrollPrev,
            'op-100': showArrowsOnHoverOnly && isHoveringLeft,
            'op-0 focus-visible:op-100': showArrowsOnHoverOnly,
          },
          dir ? 'inset-x-0 justify-center my-4 top-0' : 'top-1/2 transform -translate-y-1/2 left-0',
          classControls,
        ]"
        data-test-id="vf-carousel-arrow-left"
        :style="styleControls"
        @click.stop="scrollPrev"
      >
        <span
          class="duration rounded-r-xs forced-colors:bg-[ButtonFace]"
          :class="[
            {
              '-mx-10 group-focus-visible:mx-0': showArrowsOnHoverOnly,
              '!mx-0': showArrowsOnHoverOnly && isHoveringLeft,
            },
            classControlsInner,
          ]"
        >
          <vf-icon :dir="dir ? 'up' : 'left'" name="chevron" :size="sizeControls" />
        </span>
      </base-button>
    </slot>

    <slot v-if="showArrows" name="next" v-bind="{ active: carousel.canScrollNext, action: scrollNext }">
      <base-button
        :aria-label="$t.nextSlide"
        class="group absolute z-10 flex duration"
        :class="[
          {
            'pointer-events-none !op-0 invisible': (!loop || !scrollable) && !carousel.canScrollNext,
            'op-100': showArrowsOnHoverOnly && isHoveringRight,
            'op-0 focus-visible:op-100': showArrowsOnHoverOnly,
          },
          dir ? 'inset-x-0 justify-center my-4 bottom-0' : 'top-1/2 transform -translate-y-1/2 right-0',
          classControls,
        ]"
        data-test-id="vf-carousel-arrow-right"
        :style="styleControls"
        @click.stop="scrollNext"
      >
        <span
          class="duration rounded-l-xs forced-colors:bg-[ButtonFace]"
          :class="[
            {
              '-mx-10 group-focus-visible:mx-0': showArrowsOnHoverOnly,
              '!mx-0': showArrowsOnHoverOnly && isHoveringRight,
            },
            classControlsInner,
          ]"
        >
          <vf-icon :dir="dir ? 'down' : 'right'" name="chevron" :size="sizeControls" />
        </span>
      </base-button>
    </slot>

    <slot
      v-if="autoplay && showProgress"
      name="progressbar"
      v-bind="{ progress: intervalProgress, currentSlide, slides: carousel.slides, scrollTo, togglePlay }"
    >
      <div :class="brandClasses.progressControls">
        <vf-video-controls
          :active-index="currentSlide.index"
          :paused="interval && !interval.isActive"
          :progress="intervalProgress"
          :size="{ sm: 'sm', md: 'md' }"
          :total-videos="carousel.slides"
          @navigate="(index) => scrollTo(index, true)"
          @toggle="togglePlay"
        />
      </div>
    </slot>

    <div ref="emblaRef" class="max-w-full" :class="classWrapper">
      <component :is="asList ? 'ul' : 'div'" ref="carouselContainer" class="flex children:shrink-0" :class="classContainer" :style="styleContainer">
        <slot
          name="default"
          v-bind="{
            paused: interval && !interval.isActive,
            activeItem: currentSlide.index,
            viewedSlides,
          }"
        />
      </component>
    </div>

    <slot
      v-if="showPagination"
      name="pagination"
      v-bind="{ activeItem: currentSlide.index, total: carousel.scrollSnapCount, scrollTo }"
    />

    <!-- Screen reader announcement outside scroll container -->
    <div aria-atomic="true" :aria-live="shouldAnnounceSlide ? 'polite' : 'off'" class="sr-only">
      {{ replaceAll($t.slideOf, { current: currentSlide.index + 1, total: carousel.scrollSnapCount }) }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'
import type { CarouselElevateInstance } from '#types/components/vf/carousel-elevate'
import type { IconSizes } from '#types/sizes'

import type { EmblaCarouselType, EmblaEventType, EmblaOptionsType } from 'embla-carousel'

import type { StyleValue } from 'vue'

import emblaCarouselVue from 'embla-carousel-vue'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'

type AlignmentType = 'start' | 'center' | 'end'

const {
  active = true,
  align = 'center',
  autoplay,
  bleed,
  dir,
  dragFree = true,
  highlightCenter,
  loop,
  showPagination = true,
  showArrows = true,
  showArrowsOnHoverThreshold = 0,
  sizeControls = 'lg',
  slides,
  slidesToScroll = 'auto',
  asList,
  ...props
} = defineProps<{
  /**
   * Defines active.
   * If true carousel will be able to scroll
   */
  active?: boolean
  /**
   * Defines autoplay mode.
   * If true slides change automatically
   */
  autoplay?: boolean
  /**
   * Defines classList of items container
   */
  classContainer?: CSSClass
  /**
   * Defines classList of controls
   */
  classControls?: CSSClass
  /**
   * Defines classList of controls inner content
   */
  classControlsInner?: CSSClass
  /**
   * Defines classList of all content's wrapper excluding pagination section
   */
  classWrapper?: CSSClass
  /**
   * Expands the carousel to the full viewport width so slides bleed over both side gutters
   * while staying clipped inside the carousel itself. Slides keep snapping to the container's
   * edges and the first slide rests on its inline-start edge; requires the container to be
   * horizontally centered in the viewport (symmetric gutters or max-width with auto margins)
   */
  bleed?: boolean
  /**
   * Defines carousel direction
   */
  dir?: 'vertical'
  /**
   * Allows the drag to be free instead instead of snaping into position
   */
  dragFree?: boolean
  /**
   * Defines highlights center to scale up/down the content and change its opacity
   */
  highlightCenter?: boolean
  /**
   * Defines interval for slides changing
   * Relevant if `autoplay` prop provided only
   */
  interval?: number
  /**
   * Defines arrows visibility
   * If true arrows become always visible and that makes possible to scroll infinitely
   */
  loop?: boolean
  /**
   * Defines pagination visibility
   */
  showPagination?: boolean
  /**
   * Defines arrow visibility
   */
  showArrows?: boolean
  /**
   * Defines arrow visibility only on hover
   */
  showArrowsOnHoverOnly?: boolean
  /**
   * Defines percentage of element width that triggers arrow visibility on hover
   */
  showArrowsOnHoverThreshold?: number
  /**
   * Defines progress visibility
   */
  showProgress?: boolean
  /**
   * Defines how many slides will be scrollable when the scroll is called
   */
  slidesToScroll?: number | 'auto'
  /**
   * Defines styles of items container
   */
  styleContainer?: StyleValue
  /**
   * Defines styles of all content's wrapper excluding pagination section
   */
  styleWrapper?: StyleValue
  /**
   * Defines styles of controls container
   */
  styleControls?: StyleValue
  /**
   * Defines size of controls icon
   */
  sizeControls?: IconSizes
  /**
   * Defines HTML tag of items container
   */
  tag?: string
  /**
   * Defines the alignment of slides relative to the carousel viewport
   */
  align?: AlignmentType
  /**
   * Defines slides elements
   */
  slides?: string | HTMLElement[] | NodeListOf<HTMLElement> | null
  /**
   * Should be rendered as list
   */
  asList?: boolean
}>()

const emit = defineEmits<{
  /**
   * Emits the indexes of slides that are in view once scrolling settles, deduplicated for the
   * lifetime of the current slide set. Intended for impression tracking in parent components.
   * Slides passing by mid-scroll are not counted, and the dedupe cache resets when the slides change.
   */
  'slides-viewed': [indexes: number[]]
}>()

provide('vfCarouselElevate', true)

const emblaOptions = ref<EmblaOptionsType>({
  active,
  align,
  dragFree,
  inViewThreshold: 0.3,
  loop,
  slides,
  slidesToScroll,
})

const { brandClasses } = useAppConfig().components.vf.carousel

const [emblaRef, emblaApi] = emblaCarouselVue(
  emblaOptions,
  [WheelGesturesPlugin({ forceWheelAxis: dir === 'vertical' ? 'y' : 'x' })]
)

const { elementWidth, isOutside, x: mouseX } = useMouseInElement(emblaRef)
const isVisible = useElementVisibility(emblaRef)
const { $t } = useNuxtApp()

const interval = ref()
const intervalProgress = ref(0)

const carouselContainer = ref()
const { focused: shouldAnnounceSlide } = useFocusWithin(emblaRef)

const carousel = reactive<CarouselElevateInstance['carousel']>({
  canScrollNext: false,
  canScrollPrev: false,
  containerWidth: 0,
  scrollSnapCount: 0,
  slides: 0
})

const isHoveringLeft = computed(() => !isOutside.value
  && (!showArrowsOnHoverThreshold || mouseX.value < elementWidth.value * showArrowsOnHoverThreshold))
const isHoveringRight = computed(() => !isOutside.value
  && (!showArrowsOnHoverThreshold || mouseX.value > elementWidth.value * (1 - showArrowsOnHoverThreshold)))
const scrollable = computed(() => carousel.canScrollNext || carousel.canScrollPrev)

watchEffect(() => {
  if (carouselContainer.value)
    carousel.containerWidth = carouselContainer.value.scrollWidth
})

const currentSlide = reactive({
  index: 0,
  interval: 0
})

const viewedSlides = ref<number[]>([])

const reportViewedSlides = (emblaApi: EmblaCarouselType) => {
  if (!isVisible.value || !emblaApi.internalEngine().scrollBody.settled()) return

  const newIndexes = emblaApi.slidesInView().filter((index) => !viewedSlides.value.includes(index))

  if (!newIndexes.length) return

  viewedSlides.value = [...viewedSlides.value, ...newIndexes]
  emit('slides-viewed', newIndexes)
}

whenever(isVisible, () => {
  if (emblaApi.value) reportViewedSlides(emblaApi.value)
})

const resetViewedSlides = () => {
  viewedSlides.value = []
}

const TWEEN_FACTOR_BASE = 0.52
let tweenFactor = 0
let tweenNodes: HTMLElement[] = []

const ticker = 100

const scrollNext = () => emblaApi.value?.scrollNext()
const scrollPrev = () => emblaApi.value?.scrollPrev()

const scrollTo = (index, jump?: boolean) => {
  emblaApi.value?.scrollTo(index, jump)
  currentSlide.index = index
}

const setTweenFactor = (emblaApi: EmblaCarouselType) => {
  tweenFactor = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length
}

const setTweenNodes = (emblaApi: EmblaCarouselType) => {
  tweenNodes = emblaApi.slideNodes().map((slideNode) => slideNode.firstChild as HTMLElement)
}

const tweenScale = (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
  const engine = emblaApi.internalEngine()
  const scrollProgress = emblaApi.scrollProgress()
  const slidesInView = emblaApi.slidesInView()
  const isScrollEvent = eventName === 'scroll'

  emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
    let diffToTarget = scrollSnap - scrollProgress
    const slidesInSnap = engine.slideRegistry[snapIndex]

    slidesInSnap.forEach((slideIndex) => {
      if (isScrollEvent && !slidesInView.includes(slideIndex)) return

      if (engine.options.loop) {
        engine.slideLooper.loopPoints.forEach((loopItem) => {
          const target = loopItem.target()

          if (slideIndex === loopItem.index && target !== 0) {
            const sign = Math.sign(target)

            if (sign === -1)
              diffToTarget = scrollSnap - (1 + scrollProgress)

            if (sign === 1)
              diffToTarget = scrollSnap + (1 - scrollProgress)
          }
        })
      }

      const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor)
      const scale = Math.min(Math.max(tweenValue, 0), 1).toString()
      const tweenNode = tweenNodes[slideIndex]
      tweenNode.style.transform = `scale(${scale})`
      tweenNode.style.opacity = scale
    })
  })
}

const setupTweenScale = (emblaApi: EmblaCarouselType) => {
  setTweenNodes(emblaApi)
  setTweenFactor(emblaApi)
  tweenScale(emblaApi)

  emblaApi
    .on('reInit', setTweenNodes)
    .on('reInit', setTweenFactor)
    .on('reInit', tweenScale)
    .on('scroll', tweenScale)
    .on('slideFocus', tweenScale)
}

const togglePlay = () => {
  if (!interval.value) return
  interval.value.isActive ? interval.value.pause() : interval.value.resume()
}

const snapAfterDragFreeScroll = (emblaApi: EmblaCarouselType) => {
  if (!dragFree) return

  emblaApi.scrollTo(emblaApi.selectedScrollSnap())
}

const updateCurrentSlide = (emblaApi: EmblaCarouselType) => {
  carousel.canScrollNext = emblaApi.canScrollNext()
  carousel.canScrollPrev = emblaApi.canScrollPrev()
  carousel.scrollSnapCount = emblaApi.scrollSnapList().length
  carousel.slides = emblaApi.slideNodes().length
  currentSlide.index = emblaApi.selectedScrollSnap()
  intervalProgress.value = 0

  const selectedElement = emblaApi.slideNodes()[currentSlide.index]

  currentSlide.interval = Number(selectedElement?.getAttribute('data-interval') || 0)
}

watch([() => dragFree, () => loop, () => slidesToScroll, () => active], () => {
  emblaOptions.value = {
    ...emblaOptions.value,
    dragFree,
    loop,
    slidesToScroll,
    active
  }
})

onMounted(() => {
  if (emblaApi?.value) {
    updateCurrentSlide(emblaApi.value)

    emblaApi.value.on('select', updateCurrentSlide)
    emblaApi.value.on('reInit', updateCurrentSlide)
    emblaApi.value.on('pointerUp', snapAfterDragFreeScroll)
    emblaApi.value.on('slidesInView', reportViewedSlides)
    emblaApi.value.on('settle', reportViewedSlides)
    emblaApi.value.on('slidesChanged', resetViewedSlides)

    if (highlightCenter) setupTweenScale(emblaApi.value)
  }

  if (autoplay && interval) {
    interval.value = useIntervalFn(() => {
      intervalProgress.value += 1 / ((currentSlide.interval || props.interval || 5000) / ticker)
      if (intervalProgress.value >= 1) {
        if (!currentSlide.interval) intervalProgress.value = 0
        carousel.canScrollNext ? scrollNext() : scrollTo(0, true)
      }
    }, ticker)
  }
})

defineExpose({
  carousel,
  canScroll: computed(() => carousel.canScrollPrev || carousel.canScrollNext),
  currentIndex: computed(() => currentSlide.index),
  emblaApi,
  scrollNext,
  scrollPrev,
  scrollTo,
  viewedSlides,
  resetViewedSlides,
} satisfies CarouselElevateInstance)
</script>
